import React, { useState, useEffect } from 'react';
import PasswordModal from './PasswordModal';
import EditProfileModal from './EditProfileModal';
import { getUserId } from '../../services/authService';
import { getUserProfile, UserProfile } from '../../services/userService';
import { getUserActivities, Activity } from '../../services/activitiesService';
import { FaUser, FaEnvelope, FaCalendarAlt, FaRunning, FaStrava, FaClock, FaMountain, FaRoad, FaEdit, FaSort } from 'react-icons/fa';
import './styles.css';
import { useLocation } from 'react-router-dom';
import { getFrontendUrl, getApiUrl } from '../../utils/config';
import DatePickerCustom from '../../components/DatePickerCustom';

const Profile: React.FC = () => {
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [allActivities, setAllActivities] = useState<Activity[]>([]); // Lưu trữ tất cả hoạt động trước khi lọc
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedActivityType, setSelectedActivityType] = useState<string>('all'); // Lọc theo loại hoạt động
  const [sortOption, setSortOption] = useState<string>('newest'); // Tùy chọn sắp xếp: newest, oldest, distance-desc, distance-asc
  
  // Lấy thông tin từ location.state (nếu redirect từ trang exchange_token)
  const location = useLocation();
  
  const fetchUserData = async () => {
    try {
      console.log('Bắt đầu fetchUserData');
      setIsLoading(true);
      setErrorMessage(null);
      
      // Lấy thông tin người dùng từ API
      console.log('Gọi getUserProfile');
      const userData = await getUserProfile(null);
      console.log('Kết quả getUserProfile:', userData);
      setUserProfile(userData);

      // Lấy danh sách hoạt động
      if (userData && userData.id) {
        console.log('Gọi fetchUserActivities với userId:', userData.id);
        await fetchUserActivities(userData.id);
      } else {
        console.error('Không có userId để lấy hoạt động');
      }
    } catch (error: any) {
      console.error('Lỗi khi lấy thông tin người dùng:', error);
      setErrorMessage(error.message || 'Không thể lấy thông tin người dùng');
      
      // Kiểm tra nếu là lỗi về xác thực
      if (error.message.includes('đăng nhập') || error.message.includes('token')) {
        setErrorMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchUserActivities = async (userId: number) => {
    try {
      const activitiesData = await getUserActivities(userId);
      setAllActivities(activitiesData); // Lưu tất cả hoạt động
      setActivities(activitiesData); // Hiển thị tất cả hoạt động ban đầu
    } catch (error: any) {
      console.error('Lỗi khi lấy danh sách hoạt động:', error);
      setErrorMessage(error.message || 'Không thể lấy danh sách hoạt động');
    }
  };
  
  // Sắp xếp hoạt động sau khi lọc
  const sortActivities = (activitiesToSort: Activity[]) => {
    switch (sortOption) {
      case 'oldest':
        return [...activitiesToSort].sort((a, b) => {
          const dateA = Array.isArray(a.startDateLocal) ? new Date(a.startDateLocal[0], a.startDateLocal[1] - 1, a.startDateLocal[2]) : new Date(a.startDateLocal);
          const dateB = Array.isArray(b.startDateLocal) ? new Date(b.startDateLocal[0], b.startDateLocal[1] - 1, b.startDateLocal[2]) : new Date(b.startDateLocal);
          return dateA.getTime() - dateB.getTime();
        });
      case 'distance-desc':
        return [...activitiesToSort].sort((a, b) => b.distance - a.distance);
      case 'distance-asc':
        return [...activitiesToSort].sort((a, b) => a.distance - b.distance);
      case 'newest':
      default:
        return [...activitiesToSort].sort((a, b) => {
          const dateA = Array.isArray(a.startDateLocal) ? new Date(a.startDateLocal[0], a.startDateLocal[1] - 1, a.startDateLocal[2]) : new Date(a.startDateLocal);
          const dateB = Array.isArray(b.startDateLocal) ? new Date(b.startDateLocal[0], b.startDateLocal[1] - 1, b.startDateLocal[2]) : new Date(b.startDateLocal);
          return dateB.getTime() - dateA.getTime();
        });
    }
  };
  
  // Lọc hoạt động theo khoảng thời gian và loại hoạt động
  const filterActivities = () => {
    if (!allActivities.length) return;
    
    setIsLoading(true);
    
    // Chuyển đổi ngày từ string sang đối tượng Date
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    end.setHours(23, 59, 59, 999); // Đặt thời gian cuối ngày
    
    // Lọc hoạt động
    let filtered = [...allActivities];
    
    // Lọc theo ngày
    filtered = filtered.filter(activity => {
      // Chuyển đổi mảng startDateLocal thành đối tượng Date
      let activityDate;
      if (Array.isArray(activity.startDateLocal)) {
        const [year, month, day, hour = 0, minute = 0] = activity.startDateLocal;
        activityDate = new Date(year, month - 1, day, hour, minute);
      } else {
        activityDate = new Date(activity.startDateLocal);
      }
      
      return activityDate >= start && activityDate <= end;
    });
    
    // Lọc theo loại hoạt động
    if (selectedActivityType !== 'all') {
      filtered = filtered.filter(activity => activity.type === selectedActivityType);
    }
    
    // Sắp xếp kết quả
    filtered = sortActivities(filtered);
    
    setActivities(filtered);
    setIsLoading(false);
  };
  
  // Lọc hoạt động theo khoảng thời gian
  const filterActivitiesByDateRange = (startDate: string, endDate: string) => {
    if (userProfile) {
      setDateRange({
        startDate,
        endDate
      });
      filterActivities();
    }
  };
  
  useEffect(() => {
    // Kiểm tra đăng nhập và hiển thị thông tin debug
    console.log('Đang kiểm tra thông tin đăng nhập...');
    const authToken = localStorage.getItem('jwt_token');
    const username = localStorage.getItem('auth_username');
    console.log('Auth token:', authToken ? 'Có token' : 'Không có token');
    console.log('Username:', username);
    
    // Lấy thông tin người dùng khi component mount
    fetchUserData();
  }, []);

  useEffect(() => {
    // Kiểm tra nếu có state từ location (được redirect từ exchange_token)
    if (location.state) {
      if (location.state.stravaConnected) {
        // Nếu có thông báo kết nối thành công, cập nhật lại thông tin người dùng
        fetchUserData();
      } else if (location.state.stravaError) {
        // Nếu có lỗi từ quá trình kết nối
        setErrorMessage(location.state.errorMessage || 'Có lỗi xảy ra khi kết nối với Strava');
      }
    }
  }, [location]);

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyDateRange = () => {
    filterActivities();
  };

  const handleActivityTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedActivityType(e.target.value);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value);
  };

  const handleOpenEditProfileModal = () => {
    setEditProfileModalOpen(true);
  };

  const handleProfileUpdateSuccess = () => {
    // Cập nhật lại thông tin người dùng sau khi cập nhật thành công
    fetchUserData();
  };

  const handleConnectStrava = async () => {
    try {
      // Đánh dấu đây là luồng kết nối
      localStorage.setItem('strava_auth_flow', 'connect');
      
      // Lấy URL xác thực từ API (cần JWT token) thay vì tạo trực tiếp
      const authHeader = localStorage.getItem('jwt_token');
      if (!authHeader) {
        console.error('Bạn chưa đăng nhập');
        setErrorMessage('Vui lòng đăng nhập lại để kết nối với Strava');
        return;
      }
      
      const response = await fetch(`${getApiUrl()}/api/auth/strava/auth`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authHeader}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Không thể lấy URL xác thực Strava');
      }
      
      const data = await response.json();
      if (data.result !== 'SUCCESS' || !data.data) {
        throw new Error('Không nhận được URL xác thực từ server');
      }
      
      // URL xác thực Strava từ API
      const stravaAuthUrl = data.data;
      console.log('Đã lấy được URL xác thực Strava:', stravaAuthUrl);
      
      // Chuyển hướng đến URL xác thực Strava
      window.location.href = stravaAuthUrl;
    } catch (error: any) {
      console.error('Lỗi khi lấy URL xác thực Strava:', error);
      setErrorMessage(error.message || 'Có lỗi xảy ra khi kết nối với Strava');
    }
  };

  // Hàm format thời gian từ seconds sang hh:mm:ss
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Hàm format khoảng cách từ meters sang km
  const formatDistance = (meters: number): string => {
    return (meters / 1000).toFixed(2) + ' km';
  };

  // Hàm format ngày từ ISO string hoặc mảng [yyyy, MM, dd, ...] sang dd/MM/yyyy HH:mm
  const formatDate = (date: string | number[]): string => {
    if (Array.isArray(date)) {
      // Format từ mảng [năm, tháng, ngày, giờ, phút, ...]
      const [year, month, day, hour = 0, minute = 0] = date;
      const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
      const formattedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      return `${formattedDate} ${formattedTime}`;
    } else {
      // Format từ ISO string
      const dateObj = new Date(date);
      const formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
      const formattedTime = `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;
      return `${formattedDate} ${formattedTime}`;
    }
  };

  // Tính tổng quãng đường từ danh sách hoạt động
  const getTotalDistance = (): number => {
    return activities.reduce((sum, activity) => sum + activity.distance, 0);
  };

  // Tính tổng thời gian di chuyển từ danh sách hoạt động
  const getTotalMovingTime = (): number => {
    return activities.reduce((sum, activity) => sum + activity.movingTime, 0);
  };

  // Tính tổng độ cao tích lũy từ danh sách hoạt động
  const getTotalElevationGain = (): number => {
    return activities.reduce((sum, activity) => sum + (activity.totalElevationGain || 0), 0);
  };

  // Lấy danh sách các loại hoạt động duy nhất
  const getUniqueActivityTypes = (): string[] => {
    const types = allActivities.map(activity => activity.type);
    return ['all', ...Array.from(new Set(types))];
  };

  interface ProfileInfoRowProps {
    icon: React.ReactNode;
    label: string;
    value: string;
  }

  const ProfileInfoRow = ({ icon, label, value }: ProfileInfoRowProps) => (
    <div className="profile-info-row">
      <div className="profile-info-label">
        {icon}
        {label}
      </div>
      <div className="profile-info-value">{value}</div>
    </div>
  );

  return (
    <div className="profile-page">
      {/* Thông tin người dùng */}
      <div className="section mb-lg">
        <div className="section-header">
          <h2 className="section-title">THÔNG TIN CÁ NHÂN</h2>
        </div>
        
        <div className="section-body">
          {isLoading && !userProfile ? (
            <div className="loading-indicator">Đang tải thông tin người dùng...</div>
          ) : errorMessage && !userProfile ? (
            <div className="error-message">{errorMessage}</div>
          ) : userProfile ? (
            <div className="flex justify-between">
              <div className="profile-info">
                <ProfileInfoRow icon={<FaUser />} label="Tên đăng nhập" value={userProfile.username} />
                <ProfileInfoRow icon={<FaEnvelope />} label="Email" value={userProfile.email} />
                <ProfileInfoRow icon={<FaUser />} label="Họ và tên" value={`${userProfile.firstName} ${userProfile.lastName} `} />
                <ProfileInfoRow icon={<FaCalendarAlt />} label="Ngày tham gia" value={userProfile.createdAt ? formatDate(userProfile.createdAt) : 'N/A'} />
                <ProfileInfoRow 
                  icon={<FaRunning />} 
                  label="Tổng số hoạt động" 
                  value={activities.length > 0 ? activities.length.toString() : 'Chưa có'} 
                />
                <ProfileInfoRow 
                  icon={<FaRoad />} 
                  label="Tổng quãng đường" 
                  value={activities.length > 0 ? formatDistance(getTotalDistance()) : 'Chưa có'} 
                />
                <ProfileInfoRow 
                  icon={<FaClock />} 
                  label="Tổng thời gian" 
                  value={activities.length > 0 ? formatTime(getTotalMovingTime()) : 'Chưa có'} 
                />
                <ProfileInfoRow 
                  icon={<FaMountain />} 
                  label="Tổng độ cao" 
                  value={activities.length > 0 ? getTotalElevationGain().toFixed(0) + ' m' : 'Chưa có'} 
                />
              </div>
              
              <div className="profile-actions">
                <button 
                  onClick={handleOpenEditProfileModal}
                  className="btn btn-primary mb-sm"
                >
                  <FaEdit /> Cập nhật thông tin
                </button>
                <button 
                  onClick={() => setPasswordModalOpen(true)}
                  className="btn btn-secondary mb-sm"
                >
                  Đổi mật khẩu
                </button>
                {!userProfile.hasStravaToken && (
                  <button 
                    onClick={handleConnectStrava}
                    className="btn-strava"
                  >
                    <img 
                      src="/assets/strava/connect-buttons/btn_strava_connect_with_orange.png"
                      alt="Kết nối với Strava"
                      className="strava-connect-img"
                    />
                  </button>
                )}
                {userProfile.hasStravaToken && (
                  <div className="strava-connected mb-sm">
                    <FaStrava className="icon" />
                    <span>Đã kết nối với Strava</span>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Hoạt động của người dùng */}
      <div className="section mb-lg">
        <div className="section-header">
          <h2 className="section-title">HOẠT ĐỘNG CỦA TÔI</h2>
        </div>
        
        <div className="section-body">
          {/* Bộ lọc khoảng thời gian và loại hoạt động */}
          <div className="date-filter mb-md">
            <div className="filter-header">
              <h3>Lọc và sắp xếp hoạt động</h3>
            </div>
            <div className="date-filter-row">
              <div className="date-filter-item">
                <label>
                  <FaCalendarAlt /> Từ ngày:
                </label>
                <DatePickerCustom
                  label=""
                  value={dateRange.startDate}
                  onChange={(date) => {
                    const dateStr = date ? date.toISOString().split('T')[0] : '';
                    setDateRange(prev => ({ ...prev, startDate: dateStr }));
                  }}
                  className="date-picker"
                />
              </div>
              <div className="date-filter-item">
                <label>
                  <FaCalendarAlt /> Đến ngày:
                </label>
                <DatePickerCustom
                  label=""
                  value={dateRange.endDate}
                  onChange={(date) => {
                    const dateStr = date ? date.toISOString().split('T')[0] : '';
                    setDateRange(prev => ({ ...prev, endDate: dateStr }));
                  }}
                  className="date-picker"
                />
              </div>
              <div className="date-filter-item">
                <label>
                  <FaRunning /> Loại hoạt động:
                </label>
                <select 
                  value={selectedActivityType}
                  onChange={handleActivityTypeChange}
                >
                  {getUniqueActivityTypes().map(type => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'Tất cả' : type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="date-filter-item">
                <label>
                  <FaSort /> Sắp xếp theo:
                </label>
                <select 
                  value={sortOption}
                  onChange={handleSortChange}
                >
                  <option value="newest">Mới nhất trước</option>
                  <option value="oldest">Cũ nhất trước</option>
                  <option value="distance-desc">Quãng đường (cao đến thấp)</option>
                  <option value="distance-asc">Quãng đường (thấp đến cao)</option>
                </select>
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleApplyDateRange}
                disabled={isLoading}
              >
                {isLoading ? 'Đang tải...' : 'Áp dụng'}
              </button>
            </div>
          </div>
          
          {/* Hiển thị lỗi nếu có */}
          {errorMessage && (
            <div className="error-message mb-md">
              {errorMessage}
            </div>
          )}
          
          {/* Hiển thị thông tin tổng hợp */}
          {activities.length > 0 && (
            <>
              <div className="activities-summary mb-md">
                <div className="summary-card">
                  <div className="summary-icon"><FaRunning /></div>
                  <div className="summary-value">{activities.length}</div>
                  <div className="summary-label">Hoạt động</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><FaRoad /></div>
                  <div className="summary-value">{formatDistance(getTotalDistance())}</div>
                  <div className="summary-label">Quãng đường</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><FaClock /></div>
                  <div className="summary-value">{formatTime(getTotalMovingTime())}</div>
                  <div className="summary-label">Thời gian</div>
                </div>
              </div>
              
              {/* Danh sách hoạt động */}
              <div className="activities-list">
                <table>
                  <thead>
                    <tr>
                      <th>Ngày & Giờ</th>
                      <th>Tên hoạt động</th>
                      <th>Loại</th>
                      <th>Khoảng cách</th>
                      <th>Thời gian</th>
                      <th>Độ cao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="no-data">Không có hoạt động nào trong khoảng thời gian này</td>
                      </tr>
                    ) : (
                      activities.map((activity) => (
                        <tr key={activity.id}>
                          <td>{formatDate(activity.startDateLocal)}</td>
                          <td>{activity.name}</td>
                          <td>
                            <span className={`activity-type ${activity.type.toLowerCase()}`}>
                              {activity.type}
                            </span>
                          </td>
                          <td>
                            <span className="metric">
                              <FaRoad /> {formatDistance(activity.distance)}
                            </span>
                          </td>
                          <td>
                            <span className="metric">
                              <FaClock /> {formatTime(activity.movingTime)}
                            </span>
                          </td>
                          <td>
                            <span className="metric">
                              <FaMountain /> {activity.totalElevationGain} m
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          
          {/* Hiển thị trạng thái loading */}
          {isLoading && (
            <div className="loading-indicator">
              Đang tải dữ liệu...
            </div>
          )}
          
          {/* Hiển thị khi chưa có dữ liệu và không đang tải */}
          {!isLoading && activities.length === 0 && !errorMessage && (
            <div className="no-data-message">
              Chưa có dữ liệu hoạt động nào. Hãy kết nối với Strava và đồng bộ hoạt động của bạn.
            </div>
          )}
        </div>
      </div>

      {/* Modal đổi mật khẩu */}
      <PasswordModal isOpen={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
      
      {/* Modal cập nhật thông tin */}
      {userProfile && (
        <EditProfileModal 
          isOpen={editProfileModalOpen} 
          onClose={() => setEditProfileModalOpen(false)}
          userData={{
            username: userProfile.username,
            email: userProfile.email,
            firstName: userProfile.firstName,
            lastName: userProfile.lastName,
            profileImageUrl: userProfile.profileImageUrl,
            gender: userProfile.gender
          }}
          onSuccess={handleProfileUpdateSuccess}
        />
      )}
    </div>
  );
};

export default Profile; 